import { useBottomSheetModal } from '@gorhom/bottom-sheet';
import * as Clipboard from 'expo-clipboard';
import { useLocalSearchParams } from 'expo-router';
import { Share } from 'react-native';
import { useMutation } from 'urql';
import type { ChatMessageActionOption } from '@/components/Chat/Chat.utils';
import { useChatDetailContext } from '@/components/Chat/ChatDetailContext';
import { graphql } from '@/gql/graphql';
import { setChatInput } from '@/stores/useChatStore';
import isLink from '@/utils/isLink';
import useIsSharingAvailable from './useIsSharingAvailable';
import useUserId from './useUserId';

const DeleteMessage = graphql(/* GraphQL */ `
  mutation DeleteMessage($id: uuid!) {
    update_message_by_pk(
      pk_columns: { id: $id }
      _set: { deleted_at: "now()" }
    ) {
      id
      content
      deleted_at
    }
    update_message_media(
      where: { message_id: { _eq: $id } }
      _set: { deleted_at: "now()" }
    ) {
      affected_rows
    }
  }
`);
export default function useChatActionItems() {
  const [, deleteMessage] = useMutation(DeleteMessage);
  const inputRef = useChatDetailContext((state) => state.inputRef);
  const userId = useUserId();
  const { dismiss } = useBottomSheetModal();
  const { chatId } = useLocalSearchParams();
  const isSharingAvailable = useIsSharingAvailable();
  const getActionItems = (
    messageId: string,
    content: string | null | undefined,
    createdBy: string | undefined,
    isImage: boolean,
    editorHtml?: string,
  ) => {
    const isOwner = createdBy === userId;
    const { isContentLink, link } = isLink(content || '');
    const items: ChatMessageActionOption[] = [];
    if (isOwner && !isImage) {
      items.push({
        name: 'edit',
        text: 'Edit',
        icon: 'create-outline',
        handler: () => {
          dismiss();
          if (typeof chatId === 'string' && messageId && content) {
            setChatInput(chatId, {
              text: content,
              html: editorHtml,
              editedMessageId: messageId,
            });
            inputRef.current?.setValue(editorHtml || content);
            inputRef.current?.focus();
          }
        },
        variant: 'info',
      });
    }
    if (isSharingAvailable && isImage) {
      items.push({
        name: 'shareImage',
        text: 'Share Image...',
        icon: 'share-outline',
        handler: () => {
          dismiss();
          return Share.share({
            message: content || '',
          });
        },
        variant: 'secondary',
      });
    }
    if (isSharingAvailable && content && !isContentLink && !isImage) {
      items.push({
        name: 'shareLink',
        text: 'Share Message...',
        icon: 'share-outline',
        handler: () => {
          dismiss();
          return Share.share({
            message: content,
          });
        },
        variant: 'secondary',
      });
    }
    if (isSharingAvailable && link && isContentLink && !isImage) {
      items.push({
        name: 'shareLink',
        text: 'Share Link...',
        icon: 'share-outline',
        handler: () => {
          dismiss();
          return Share.share({
            message: link,
          });
        },
        variant: 'secondary',
      });
    }
    if (content) {
      items.push({
        name: 'copy',
        text: 'Copy',
        icon: 'copy-outline',
        handler: () => {
          dismiss();
          Clipboard.setStringAsync(content);
        },
        variant: 'secondary',
      });
    }
    if (isOwner && !isImage) {
      items.push({
        name: 'delete',
        text: 'Delete',
        icon: 'trash-outline',
        handler: () => {
          dismiss();
          deleteMessage({
            id: messageId,
          });
        },
        variant: 'error',
      });
    }
    return items;
  };
  return getActionItems;
}
